/**
 * useSpatialPush — Unified iOS 26 Spatial Layering push/pop animation hook.
 *
 * Extracted from SpatialOverlay (tab-level) and stackTransitionPlugin (Stackflow).
 * Owns: phase state machine, GSAP timelines, edge-swipe gesture, deep-link snap.
 * Does NOT own: DOM rendering, mount/unmount, navigation.
 *
 * All animation constants from spatialTransition.ts (single source of truth).
 */

import { useRef, useEffect, useLayoutEffect, type RefObject } from 'react';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { haptic } from '@/utils/haptics';
import {
  PARENT_X_RATIO,
  PARENT_RADIUS,
  CHILD_RADIUS,
  THRESHOLD_RATIO,
  VELOCITY_THRESHOLD,
  DURATION_PUSH,
  DURATION_CLOSE,
  DURATION_REBOUND,
  EASE_PUSH,
  EASE_SCRIM,
  EASE_CLOSE,
  EASE_REBOUND,
  getIsRTL,
  isInEdgeZone,
  getClientX,
} from '@/utils/spatialTransition';

gsap.registerPlugin(Observer);

type Phase = 'idle' | 'open' | 'closing';

interface UseSpatialPushOptions {
  parentRef: RefObject<HTMLElement | null>;
  childRef: RefObject<HTMLElement | null>;
  scrimRef: RefObject<HTMLElement | null>;
  isActive: boolean;
  mounted?: boolean;
  onPop: () => void;
  onBeforePush?: (parentEl: HTMLElement) => void;
  onAfterPop?: (parentEl: HTMLElement) => void;
  stopEdgePropagation?: boolean;
  /** Identity of the current child — forces a new push animation when it
   *  changes even if isActive stays true (e.g. Stackflow replace). */
  pushKey?: string;
}

export function useSpatialPush({
  parentRef,
  childRef,
  scrimRef,
  isActive,
  mounted = true,
  onPop,
  onBeforePush,
  onAfterPop,
  stopEdgePropagation = false,
  pushKey,
}: UseSpatialPushOptions) {
  // Callback refs — updated every render, never in effect deps
  const onPopRef = useRef(onPop);
  const onBeforePushRef = useRef(onBeforePush);
  const onAfterPopRef = useRef(onAfterPop);
  onPopRef.current = onPop;
  onBeforePushRef.current = onBeforePush;
  onAfterPopRef.current = onAfterPop;

  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const observerRef = useRef<ReturnType<typeof Observer.create> | null>(null);
  const phaseRef = useRef<Phase>('idle');
  const gestureDidPopRef = useRef(false);
  // Deep link: captures whether isActive was true at mount time
  const isDeepLinkRef = useRef(isActive);
  // Track pushKey so we detect child replacement (isActive stays true, key changes)
  const prevPushKeyRef = useRef(pushKey ?? '');

  const isRTL = getIsRTL();

  // ── Effect 1: Push / Pop animation ──
  // useLayoutEffect so GSAP positions elements BEFORE the browser paints.
  // Without this the browser paints one frame with the old DOM state,
  // causing a visible flash/stutter at the start of the push animation.
  useLayoutEffect(() => {
    if (!mounted) return;

    const parentEl = parentRef.current;
    const childEl = childRef.current;
    const scrimEl = scrimRef.current;
    if (!parentEl || !childEl || !scrimEl) {
      // Elements unavailable (unmounted during transition) — force idle
      if (phaseRef.current !== 'idle') phaseRef.current = 'idle';
      // Clear stale gestureDidPop so it doesn't poison the next cycle
      gestureDidPopRef.current = false;
      // Defensive: clear overflow on whichever elements are still available
      if (parentEl) parentEl.style.overflow = '';
      if (childEl) childEl.style.overflow = '';
      return;
    }

    const screenWidth = window.innerWidth;
    const parentTargetX = isRTL
      ? screenWidth * PARENT_X_RATIO
      : -screenWidth * PARENT_X_RATIO;

    const currentKey = pushKey ?? '';
    const keyChanged = currentKey !== prevPushKeyRef.current;

    // ── PUSH ──
    // Trigger on: isActive went true, OR pushKey changed while active
    if (isActive && (phaseRef.current !== 'open' || keyChanged)) {
      const wasOpen = phaseRef.current === 'open';
      if (timelineRef.current) {
        timelineRef.current.kill();
        // Reset phase & clear overflow left by killed animation (Bug 8 & 6)
        phaseRef.current = 'idle';
        parentEl.style.overflow = '';
        childEl.style.overflow = '';
      }
      const isReplace = wasOpen && keyChanged;
      phaseRef.current = 'open';
      prevPushKeyRef.current = currentKey;
      // Entering push clears any stale gestureDidPop from a previous cycle
      gestureDidPopRef.current = false;

      if (isDeepLinkRef.current || isReplace) {
        // Deep link or replace: snap to open, no animation.
        // Replace (switching conversations while already open) must be instant
        // to avoid a slide-in flicker on desktop.
        isDeepLinkRef.current = false;
        onBeforePushRef.current?.(parentEl);
        gsap.set(parentEl, { x: parentTargetX, borderRadius: PARENT_RADIUS });
        parentEl.style.overflow = 'hidden';
        // Scrim hidden — child fully covers it; avoids subtle dark tint
        gsap.set(scrimEl, { opacity: 1, visibility: 'hidden' });
        gsap.set(childEl, { x: 0, borderRadius: 0, visibility: 'visible' });
        return;
      }
      isDeepLinkRef.current = false;

      // Animated push
      onBeforePushRef.current?.(parentEl);
      gsap.set(scrimEl, { visibility: 'visible', opacity: 0 });
      const startX = isRTL ? -screenWidth : screenWidth;
      gsap.set(childEl, { visibility: 'visible', x: startX, borderRadius: CHILD_RADIUS });
      childEl.style.overflow = 'hidden';
      gsap.set(parentEl, { x: 0, borderRadius: 0 });

      const tl = gsap.timeline();
      timelineRef.current = tl;

      tl.to(
        parentEl,
        {
          x: parentTargetX,
          borderRadius: PARENT_RADIUS,
          duration: DURATION_PUSH,
          ease: EASE_PUSH,
          onUpdate: () => {
            parentEl.style.overflow = 'hidden';
          },
        },
        0,
      );
      tl.to(
        scrimEl,
        { opacity: 1, duration: DURATION_PUSH, ease: EASE_SCRIM },
        0,
      );
      tl.to(
        childEl,
        {
          x: 0,
          borderRadius: 0,
          duration: DURATION_PUSH,
          ease: EASE_PUSH,
          onComplete: () => {
            childEl.style.overflow = '';
            // Hide scrim after push completes — child fully covers it.
            // Prevents a subtle dark tint on desktop where compositing
            // can leak the scrim through the opaque child layer.
            gsap.set(scrimEl, { visibility: 'hidden' });
          },
        },
        0,
      );
      return;
    }

    // ── Track key even when not pushing (so stale keys don't trigger false pushes) ──
    if (keyChanged) {
      prevPushKeyRef.current = currentKey;
    }

    // ── POP (programmatic — browser back, header back, tab switch) ──
    if (!isActive && phaseRef.current === 'open') {
      if (timelineRef.current) {
        timelineRef.current.kill();
        // Clear overflow left by killed animation (Bug 6)
        parentEl.style.overflow = '';
        childEl.style.overflow = '';
      }

      // Gesture already animated the pop — instant cleanup
      if (gestureDidPopRef.current) {
        gestureDidPopRef.current = false;
        gsap.set(parentEl, { x: 0, borderRadius: 0, clearProps: 'transform' });
        parentEl.style.overflow = '';
        gsap.set(childEl, { visibility: 'hidden' });
        gsap.set(scrimEl, { visibility: 'hidden', opacity: 0 });
        phaseRef.current = 'idle';
        onAfterPopRef.current?.(parentEl);
        return;
      }

      phaseRef.current = 'closing';
      const targetX = isRTL ? -screenWidth : screenWidth;

      // Re-show scrim for the pop animation (hidden after push)
      gsap.set(scrimEl, { visibility: 'visible', opacity: 1 });

      const tl = gsap.timeline({
        onComplete: () => {
          gsap.set(parentEl, { x: 0, borderRadius: 0, clearProps: 'transform' });
          parentEl.style.overflow = '';
          gsap.set(childEl, { visibility: 'hidden' });
          gsap.set(scrimEl, { visibility: 'hidden', opacity: 0 });
          phaseRef.current = 'idle';
          onAfterPopRef.current?.(parentEl);
        },
      });
      timelineRef.current = tl;

      tl.to(
        parentEl,
        { x: 0, borderRadius: 0, duration: DURATION_PUSH, ease: EASE_PUSH },
        0,
      );
      tl.to(
        scrimEl,
        { opacity: 0, duration: DURATION_PUSH, ease: EASE_SCRIM },
        0,
      );
      tl.to(
        childEl,
        {
          x: targetX,
          borderRadius: CHILD_RADIUS,
          duration: DURATION_PUSH,
          ease: EASE_PUSH,
        },
        0,
      );
      return;
    }

    // ── IDLE with !isActive — ensure gestureDidPop is cleared ──
    if (!isActive) {
      gestureDidPopRef.current = false;
    }
  }, [isActive, mounted, isRTL, pushKey, parentRef, childRef, scrimRef]);

  // ── Effect 2: Edge-swipe gesture ──
  useEffect(() => {
    if (!mounted || !isActive || phaseRef.current !== 'open') return;

    const childEl = childRef.current;
    const parentEl = parentRef.current;
    const scrimEl = scrimRef.current;
    if (!childEl || !parentEl || !scrimEl) return;

    const screenWidth = window.innerWidth;
    const parentTargetX = isRTL
      ? screenWidth * PARENT_X_RATIO
      : -screenWidth * PARENT_X_RATIO;

    const proxy = { progress: 0 };
    let isValidSwipe = false;
    let isDragging = false;
    let isClosing = false;
    let hasTriggeredHaptic = false;
    let dragStartX = 0;
    let startedInEdge = false;

    const setChildX = gsap.quickSetter(childEl, 'x', 'px');
    const setChildRadius = gsap.quickSetter(childEl, 'borderRadius', 'px');
    const setParentX = gsap.quickSetter(parentEl, 'x', 'px');
    const setParentRadius = gsap.quickSetter(parentEl, 'borderRadius', 'px');
    const setScrimOpacity = gsap.quickSetter(scrimEl, 'opacity');

    function updateLayers(p: number) {
      const c = Math.max(0, Math.min(1, p));

      // Child: slide offscreen
      setChildX(isRTL ? -screenWidth * c : screenWidth * c);
      setChildRadius(CHILD_RADIUS * c);
      childEl.style.overflow = c > 0.01 ? 'hidden' : '';

      // Parent: parallax recover
      setParentX(parentTargetX * (1 - c));
      setParentRadius(PARENT_RADIUS * (1 - c));

      // Scrim: fade out
      setScrimOpacity(1 - c);
    }

    // When nested inside another spatial layer (e.g. Stackflow inside
    // SpatialOverlay), we must stop BOTH touchstart AND the synthesized
    // pointerdown from bubbling.  The Observer's onPress only handles one
    // event type; the browser fires pointerdown separately on mobile.
    // Without this, the parent layer's Observer also tracks the drag,
    // causing conflicting animations and a visual "rebound".
    let extraListenerCleanup: (() => void) | null = null;
    if (stopEdgePropagation) {
      const stopIfEdge = (e: Event) => {
        const cx =
          e instanceof TouchEvent
            ? e.touches[0]?.clientX ?? 0
            : (e as PointerEvent).clientX ?? 0;
        if (isInEdgeZone(cx, screenWidth, isRTL)) {
          e.stopPropagation();
        }
      };
      childEl.addEventListener('touchstart', stopIfEdge);
      childEl.addEventListener('pointerdown', stopIfEdge);
      extraListenerCleanup = () => {
        childEl.removeEventListener('touchstart', stopIfEdge);
        childEl.removeEventListener('pointerdown', stopIfEdge);
      };
    }

    observerRef.current = Observer.create({
      target: childEl,
      type: 'touch,pointer',
      dragMinimum: 10,
      tolerance: 10,
      preventDefault: false,

      onPress: (self) => {
        if (isClosing) return;
        dragStartX = self.x || 0;

        const evt = self.event;
        if (evt) {
          startedInEdge = isInEdgeZone(getClientX(evt), screenWidth, isRTL);
        }
      },

      onDragStart: (self) => {
        if (isClosing || !startedInEdge) return;

        const deltaX = (self.x || 0) - dragStartX;
        const deltaY = (self.y || 0) - (self.startY || 0);
        if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

        const isBackDirection = isRTL ? deltaX < 0 : deltaX > 0;
        if (!isBackDirection) return;

        isValidSwipe = true;
        isDragging = true;
        hasTriggeredHaptic = false;
        dragStartX = self.x || 0;

        // Re-show scrim for the swipe-back animation (hidden after push)
        gsap.set(scrimEl, { visibility: 'visible', opacity: 1 });

        childEl.style.userSelect = 'none';
        document.body.style.userSelect = 'none';
      },

      onDrag: (self) => {
        if (!isValidSwipe || !isDragging || isClosing) return;

        const rawDelta = (self.x || 0) - dragStartX;
        const progress = isRTL
          ? Math.min(1, Math.max(0, -rawDelta / screenWidth))
          : Math.min(1, Math.max(0, rawDelta / screenWidth));

        proxy.progress = progress;
        updateLayers(progress);

        if (progress >= THRESHOLD_RATIO && !hasTriggeredHaptic) {
          haptic.nudge();
          hasTriggeredHaptic = true;
        } else if (progress < THRESHOLD_RATIO) {
          hasTriggeredHaptic = false;
        }
      },

      onDragEnd: (self) => {
        childEl.style.userSelect = '';
        document.body.style.userSelect = '';

        if (!isValidSwipe || isClosing) {
          isDragging = false;
          return;
        }

        const progress = proxy.progress;
        const backVelocity = isRTL
          ? -(self.velocityX || 0)
          : self.velocityX || 0;
        const shouldPop =
          progress > THRESHOLD_RATIO || backVelocity > VELOCITY_THRESHOLD;

        if (shouldPop) {
          isClosing = true;
          if (observerRef.current) observerRef.current.disable();

          gsap.to(proxy, {
            progress: 1,
            duration: DURATION_CLOSE,
            ease: EASE_CLOSE,
            onUpdate: () => updateLayers(proxy.progress),
            onComplete: () => {
              haptic.light();
              gsap.set(parentEl, {
                x: 0,
                borderRadius: 0,
                clearProps: 'transform',
              });
              parentEl.style.overflow = '';
              gsap.set(childEl, { visibility: 'hidden' });
              gsap.set(scrimEl, { visibility: 'hidden', opacity: 0 });
              phaseRef.current = 'idle';
              gestureDidPopRef.current = true;
              onAfterPopRef.current?.(parentEl);
              onPopRef.current();
            },
          });
        } else if (progress > 0) {
          gsap.to(proxy, {
            progress: 0,
            duration: DURATION_REBOUND,
            ease: EASE_REBOUND,
            onUpdate: () => updateLayers(proxy.progress),
          });
        }

        isDragging = false;
        isValidSwipe = false;
      },
    });

    return () => {
      extraListenerCleanup?.();
      if (observerRef.current) {
        observerRef.current.kill();
        observerRef.current = null;
      }
    };
  }, [isActive, mounted, isRTL, pushKey, parentRef, childRef, scrimRef, stopEdgePropagation]);

  // ── Effect 3: Cleanup on unmount ──
  // Must reset ALL inline styles on the parent shell, scrim, and child
  // so that resizing from mobile → desktop doesn't leave stale transforms.
  useEffect(
    () => () => {
      if (timelineRef.current) timelineRef.current.kill();
      if (observerRef.current) {
        observerRef.current.kill();
        observerRef.current = null;
      }

      const parentEl = parentRef.current;
      const childEl = childRef.current;
      const scrimEl = scrimRef.current;

      if (parentEl) {
        gsap.set(parentEl, { clearProps: 'transform,borderRadius' });
        parentEl.style.overflow = '';
        parentEl.style.height = '';
      }
      if (childEl) {
        gsap.set(childEl, { clearProps: 'transform,borderRadius,visibility' });
      }
      if (scrimEl) {
        gsap.set(scrimEl, { clearProps: 'opacity,visibility' });
      }
      phaseRef.current = 'idle';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
